import React, { useState, useEffect } from 'react';
import Sidebar from '../components/shared/Sidebar';
import DataGrid, { GridColumn, GridRow } from '../components/data/DataGrid';
import useAuthStore from '../store/authStore';
import { HierarchyLevel, HierarchyNode } from '../types/hierarchy';
import '../components/Modal.css';

const HierarchyManagementPage: React.FC = () => {
  const { user } = useAuthStore();
  const [levels, setLevels] = useState<HierarchyLevel[]>([]);
  const [nodes, setNodes] = useState<HierarchyNode[]>([]);
  const [parentNodeOptions, setParentNodeOptions] = useState<HierarchyNode[]>([]);
  const [selectedLevel, setSelectedLevel] = useState<HierarchyLevel | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCreateLevelForm, setShowCreateLevelForm] = useState(false);
  const [showCreateNodeForm, setShowCreateNodeForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editingLevel, setEditingLevel] = useState<HierarchyLevel | null>(null);
  const [editingNode, setEditingNode] = useState<HierarchyNode | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<{ type: 'level' | 'node'; item: HierarchyLevel | HierarchyNode } | null>(null);

  const [levelFormData, setLevelFormData] = useState({
    name: '',
    level_order: 1
  });

  const [nodeFormData, setNodeFormData] = useState({
    name: '',
    parent_node_id: ''
  });

  useEffect(() => {
    fetchLevels();
  }, []);

  useEffect(() => {
    if (selectedLevel) {
      fetchNodes(selectedLevel.id);
      fetchParentNodeOptions(selectedLevel.level_order);
    } else {
      setNodes([]);
      setParentNodeOptions([]);
    }
  }, [selectedLevel]);

  const fetchLevels = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/hierarchy/levels`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch hierarchy levels: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setLevels(data.data);
      } else {
        throw new Error(data.message || 'Failed to fetch hierarchy levels');
      }
    } catch (err: any) {
      setError(err.message);
      console.error('Error fetching hierarchy levels:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchNodes = async (levelId: number) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/hierarchy/levels/${levelId}/nodes`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch hierarchy nodes: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setNodes(data.data);
      } else {
        throw new Error(data.message || 'Failed to fetch hierarchy nodes');
      }
    } catch (err: any) {
      setError(err.message);
      console.error('Error fetching hierarchy nodes:', err);
    }
  };

  const fetchParentNodeOptions = async (currentLevelOrder: number) => {
    try {
      // Get all levels with lower level_order (higher in hierarchy)
      const higherLevels = levels.filter(level => level.level_order < currentLevelOrder);
      
      if (higherLevels.length === 0) {
        setParentNodeOptions([]);
        return;
      }
      
      // Fetch nodes from all higher levels
      const allParentNodes: HierarchyNode[] = [];
      
      for (const level of higherLevels) {
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/hierarchy/levels/${level.id}/nodes`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            // Add level name to each node for better display
            const nodesWithLevel = data.data.map((node: HierarchyNode) => ({
              ...node,
              levelName: level.name
            }));
            allParentNodes.push(...nodesWithLevel);
          }
        }
      }
      
      setParentNodeOptions(allParentNodes);
    } catch (err: any) {
      console.error('Error fetching parent node options:', err);
      setParentNodeOptions([]);
    }
  };

  const handleLevelInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setLevelFormData(prev => ({
      ...prev,
      [name]: name === 'level_order' ? parseInt(value) || 1 : value
    }));
  };

  const handleNodeInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setNodeFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCreateLevel = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setError(null);
    setSuccess(null);

    try {
      if (!levelFormData.name) {
        throw new Error('Level name is required');
      }

      const url = editingLevel 
        ? `${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/hierarchy/levels/${editingLevel.id}`
        : `${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/hierarchy/levels`;
      
      const method = editingLevel ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(levelFormData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || `Failed to ${editingLevel ? 'update' : 'create'} hierarchy level`);
      }

      if (data.success) {
        setSuccess(`Hierarchy level "${levelFormData.name}" ${editingLevel ? 'updated' : 'created'} successfully!`);
        setLevelFormData({ name: '', level_order: 1 });
        setShowCreateLevelForm(false);
        setEditingLevel(null);
        fetchLevels();
      } else {
        throw new Error(data.message || `Failed to ${editingLevel ? 'update' : 'create'} hierarchy level`);
      }
    } catch (err: any) {
      setError(err.message);
      console.error(`Error ${editingLevel ? 'updating' : 'creating'} hierarchy level:`, err);
    } finally {
      setCreating(false);
    }
  };

  const handleCreateNode = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setError(null);
    setSuccess(null);

    try {
      if (!nodeFormData.name || !selectedLevel) {
        throw new Error('Node name and level selection are required');
      }

      const payload = {
        name: nodeFormData.name,
        hierarchy_level_id: selectedLevel.id,
        parent_node_id: nodeFormData.parent_node_id ? parseInt(nodeFormData.parent_node_id) : undefined
      };


      const url = editingNode 
        ? `${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/hierarchy/nodes/${editingNode.id}`
        : `${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/hierarchy/nodes`;
      
      const method = editingNode ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || `Failed to ${editingNode ? 'update' : 'create'} hierarchy node`);
      }

      if (data.success) {
        setSuccess(`Hierarchy node "${nodeFormData.name}" ${editingNode ? 'updated' : 'created'} successfully!`);
        setNodeFormData({ name: '', parent_node_id: '' });
        setShowCreateNodeForm(false);
        setEditingNode(null);
        if (selectedLevel) {
          fetchNodes(selectedLevel.id);
        }
      } else {
        throw new Error(data.message || `Failed to ${editingNode ? 'update' : 'create'} hierarchy node`);
      }
    } catch (err: any) {
      setError(err.message);
      console.error(`Error ${editingNode ? 'updating' : 'creating'} hierarchy node:`, err);
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (type: 'level' | 'node', id: number) => {
    setError(null);
    setSuccess(null);

    try {
      const url = type === 'level' 
        ? `${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/hierarchy/levels/${id}`
        : `${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/hierarchy/nodes/${id}`;

      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || `Failed to delete ${type}`);
      }

      setSuccess(`${type === 'level' ? 'Hierarchy level' : 'Hierarchy node'} deleted successfully!`);
      setShowDeleteConfirm(null);
      
      if (type === 'level') {
        fetchLevels();
        setSelectedLevel(null);
      } else if (selectedLevel) {
        fetchNodes(selectedLevel.id);
      }
    } catch (err: any) {
      setError(err.message || `Failed to delete ${type}`);
      setShowDeleteConfirm(null);
    }
  };

  const startEditLevel = (level: HierarchyLevel) => {
    setEditingLevel(level);
    setLevelFormData({
      name: level.name,
      level_order: level.level_order
    });
    setShowCreateLevelForm(true);
  };

  const startEditNode = (node: HierarchyNode) => {
    setEditingNode(node);
    setNodeFormData({
      name: node.name,
      parent_node_id: node.parent_node_id ? String(node.parent_node_id) : ''
    });
    setShowCreateNodeForm(true);
  };

  const cancelLevelEdit = () => {
    setEditingLevel(null);
    setLevelFormData({ name: '', level_order: 1 });
    setShowCreateLevelForm(false);
  };

  const cancelNodeEdit = () => {
    setEditingNode(null);
    setNodeFormData({ name: '', parent_node_id: '' });
    setShowCreateNodeForm(false);
  };

  return (
    <div className="dashboard-page">
      <Sidebar />
      <div className="dashboard-main">
        <div className="dashboard-content">
          <div className="page-header">
            <h1>Hierarchy Management</h1>
            <p>
              {user?.is_super_admin 
                ? 'Manage hierarchy levels and nodes across all organizations' 
                : 'Manage hierarchy levels and nodes for your organization'
              }
            </p>
          </div>

          {error && (
            <div className="alert alert-error">
              <span>{error}</span>
              <button onClick={() => setError(null)} className="alert-close">×</button>
            </div>
          )}

          {success && (
            <div className="alert alert-success">
              <span>{success}</span>
              <button onClick={() => setSuccess(null)} className="alert-close">×</button>
            </div>
          )}

          {/* Hierarchy Levels Section */}
          <div className="card" style={{ marginBottom: '24px' }}>
            <div className="card-header">
              <h2>Hierarchy Levels ({levels.length})</h2>
              <button 
                className="primary-button"
                onClick={() => setShowCreateLevelForm(true)}
                disabled={creating}
              >
                Create Level
              </button>
            </div>
            <div className="card-content">
              {loading ? (
                <div className="loading-state">
                  <div className="loading-spinner"></div>
                  <p>Loading hierarchy levels...</p>
                </div>
              ) : levels.length === 0 ? (
                <div className="empty-state">
                  <h3>No Hierarchy Levels Yet</h3>
                  <p>Create your first hierarchy level to get started.</p>
                  <button 
                    className="primary-button"
                    onClick={() => setShowCreateLevelForm(true)}
                  >
                    Create First Level
                  </button>
                </div>
              ) : (
                <DataGrid
                  columns={[
                    {
                      field: 'level_order',
                      headerName: 'Order',
                      width: 80,
                      type: 'number'
                    },
                    {
                      field: 'name',
                      headerName: 'Level Name',
                      width: 200
                    },
                    {
                      field: 'organizationName',
                      headerName: 'Organization',
                      width: 180,
                      renderCell: (row: GridRow) => (
                        <span>{row.organizationName || 'Unassigned'}</span>
                      )
                    },
                    {
                      field: 'created_at',
                      headerName: 'Created',
                      width: 120,
                      type: 'date',
                      renderCell: (row: GridRow) => (
                        <span>{row.created_at ? new Date(row.created_at).toLocaleDateString() : '-'}</span>
                      )
                    },
                    {
                      field: 'actions',
                      headerName: 'Actions',
                      renderCell: (row: GridRow) => (
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', whiteSpace: 'nowrap' }}>
                          <button 
                            className="primary-button"
                            onClick={() => setSelectedLevel(row as HierarchyLevel)}
                            style={{ fontSize: '13px', padding: '8px 12px' }}
                          >
                            {selectedLevel?.id === row.id ? 'Selected' : 'Select'}
                          </button>
                          <button 
                            className="secondary-button"
                            onClick={() => startEditLevel(row as HierarchyLevel)}
                            style={{ fontSize: '13px', padding: '8px 12px' }}
                          >
                            Edit
                          </button>
                          <button 
                            className="delete-button"
                            onClick={() => setShowDeleteConfirm({ type: 'level', item: row as HierarchyLevel })}
                            style={{ fontSize: '13px', padding: '8px 12px' }}
                          >
                            Delete
                          </button>
                        </div>
                      )
                    }
                  ]}
                  rows={levels.map(level => ({ ...level }))}
                  height="300px"
                  loading={loading}
                  className="levels-grid"
                />
              )}
            </div>
          </div>

          {/* Hierarchy Nodes Section */}
          {selectedLevel && (
            <div className="card">
              <div className="card-header">
                <h2>Nodes in "{selectedLevel.name}" ({nodes.length})</h2>
                <button 
                  className="primary-button"
                  onClick={() => setShowCreateNodeForm(true)}
                  disabled={creating}
                >
                  Create Node
                </button>
              </div>
              <div className="card-content">
                {nodes.length === 0 ? (
                  <div className="empty-state">
                    <h3>No Nodes Yet</h3>
                    <p>Create your first node in this level.</p>
                    <button 
                      className="primary-button"
                      onClick={() => setShowCreateNodeForm(true)}
                    >
                      Create First Node
                    </button>
                  </div>
                ) : (
                  <DataGrid
                    columns={[
                      {
                        field: 'name',
                        headerName: 'Node Name',
                        width: 200
                      },
                      {
                        field: 'parentNodeName',
                        headerName: 'Parent Node',
                        width: 180,
                        renderCell: (row: GridRow) => (
                          <span>{row.parentNodeName || 'Root'}</span>
                        )
                      },
                      {
                        field: 'created_at',
                        headerName: 'Created',
                        width: 120,
                        type: 'date',
                        renderCell: (row: GridRow) => (
                          <span>{row.created_at ? new Date(row.created_at).toLocaleDateString() : '-'}</span>
                        )
                      },
                      {
                        field: 'actions',
                        headerName: 'Actions',
                        renderCell: (row: GridRow) => (
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', whiteSpace: 'nowrap' }}>
                            <button 
                              className="secondary-button"
                              onClick={() => startEditNode(row as HierarchyNode)}
                              style={{ fontSize: '13px', padding: '8px 12px' }}
                            >
                              Edit
                            </button>
                            <button 
                              className="delete-button"
                              onClick={() => setShowDeleteConfirm({ type: 'node', item: row as HierarchyNode })}
                              style={{ fontSize: '13px', padding: '8px 12px' }}
                            >
                              Delete
                            </button>
                          </div>
                        )
                      }
                    ]}
                    rows={nodes.map(node => ({ ...node }))}
                    height="300px"
                    loading={false}
                    className="nodes-grid"
                  />
                )}
              </div>
            </div>
          )}

          {/* Create/Edit Level Modal */}
          {showCreateLevelForm && (
            <div className="modal-overlay" onClick={() => cancelLevelEdit()}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>{editingLevel ? 'Edit Hierarchy Level' : 'Create New Hierarchy Level'}</h2>
                </div>
                <div className="modal-body">
                  <form onSubmit={handleCreateLevel}>
                    <div className="form-group">
                      <label htmlFor="level-name">Level Name *</label>
                      <input
                        type="text"
                        id="level-name"
                        name="name"
                        value={levelFormData.name}
                        onChange={handleLevelInputChange}
                        placeholder="e.g., Region, Department, Team"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="level-order">Level Order *</label>
                      <input
                        type="number"
                        id="level-order"
                        name="level_order"
                        value={levelFormData.level_order}
                        onChange={handleLevelInputChange}
                        min="1"
                        required
                      />
                      <small style={{ color: '#888', fontSize: '12px' }}>
                        Lower numbers appear first (1 = highest level)
                      </small>
                    </div>
                  </form>
                </div>
                <div className="modal-actions">
                  <button 
                    type="submit" 
                    className="primary-button"
                    onClick={handleCreateLevel}
                    disabled={creating}
                  >
                    {creating ? (editingLevel ? 'Updating...' : 'Creating...') : (editingLevel ? 'Update Level' : 'Create Level')}
                  </button>
                  <button 
                    type="button" 
                    className="secondary-button"
                    onClick={cancelLevelEdit}
                    disabled={creating}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Create/Edit Node Modal */}
          {showCreateNodeForm && selectedLevel && (
            <div className="modal-overlay" onClick={() => cancelNodeEdit()}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>{editingNode ? 'Edit Hierarchy Node' : `Create Node in "${selectedLevel.name}"`}</h2>
                </div>
                <div className="modal-body">
                  <form onSubmit={handleCreateNode}>
                    <div className="form-group">
                      <label htmlFor="node-name">Node Name *</label>
                      <input
                        type="text"
                        id="node-name"
                        name="name"
                        value={nodeFormData.name}
                        onChange={handleNodeInputChange}
                        placeholder="e.g., North Region, Sales Team"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="parent-node">Parent Node</label>
                      <select
                        id="parent-node"
                        name="parent_node_id"
                        value={nodeFormData.parent_node_id}
                        onChange={handleNodeInputChange}
                      >
                        <option value="">None (Root Level)</option>
                        {parentNodeOptions.map(node => (
                          <option key={node.id} value={node.id}>
                            {node.name} ({node.levelName})
                          </option>
                        ))}
                      </select>
                    </div>
                  </form>
                </div>
                <div className="modal-actions">
                  <button 
                    type="submit" 
                    className="primary-button"
                    onClick={handleCreateNode}
                    disabled={creating}
                  >
                    {creating ? (editingNode ? 'Updating...' : 'Creating...') : (editingNode ? 'Update Node' : 'Create Node')}
                  </button>
                  <button 
                    type="button" 
                    className="secondary-button"
                    onClick={cancelNodeEdit}
                    disabled={creating}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Delete Confirmation Modal */}
          {showDeleteConfirm && (
            <div className="modal-overlay" onClick={() => setShowDeleteConfirm(null)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>Confirm Delete</h2>
                </div>
                <div className="modal-body">
                  <p>Are you sure you want to delete the {showDeleteConfirm.type} <strong>"{showDeleteConfirm.item.name}"</strong>?</p>
                  <p style={{ color: '#ff6b6b', fontSize: '14px' }}>
                    ⚠️ This action cannot be undone. {showDeleteConfirm.type === 'level' ? 'All nodes in this level will also be deleted.' : 'All child nodes will also be deleted.'}
                  </p>
                </div>
                <div className="modal-actions">
                  <button 
                    className="delete-button"
                    onClick={() => handleDelete(showDeleteConfirm.type, showDeleteConfirm.item.id)}
                  >
                    Delete {showDeleteConfirm.type === 'level' ? 'Level' : 'Node'}
                  </button>
                  <button 
                    className="secondary-button"
                    onClick={() => setShowDeleteConfirm(null)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HierarchyManagementPage;